import './LearningModule.scss';
import DOMPurify from 'dompurify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useServices } from 'index';
import { useParams, useSearchParams } from 'react-router-dom';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { TableOfContents } from 'pages/TableOfContents/TableOfContents';
import { GetPremiumCTA } from 'components/PremiumCTA/GetPremiumCTA';
import { useState } from 'react';
import { LearningModuleContentsEntry, LearningModuleLesson } from 'types/learningHub/moduleContents';
import { CalculatorKind } from 'types/calculator/calculatorType';
import { VideoLesson } from './TypeSpecificComponents/VideoLesson/VideoLesson';
import { CalculatorLesson } from './TypeSpecificComponents/CalculatorLesson/CalculatorLesson';
import { PictureLesson } from './TypeSpecificComponents/PictureLesson/PictureLesson';
import { TextLesson } from './TypeSpecificComponents/TextLesson/TextLesson';
import { ChapterTest } from './TypeSpecificComponents/ChapterTest/ChapterTest';
import { CertificationExam } from './TypeSpecificComponents/CertificationExam/CertificationExam';

import {
  getFirstEntryInModule,
  getSelectedEntry,
  isCertificationEntry,
  isChapterTestEntry,
  isLessonEntry,
} from 'utilities/learningModuleHelpers';
import { useIsUserMember } from 'queryHooks/permissions';
import { useGetModuleDetails, useGetModuleTableOfContents } from 'queryHooks/learningHub';
import { LearningModuleHeader } from './LearningModuleHeader/LearningModuleHeader';
import { ContentHeader } from './TypeSpecificComponents/ContentHeader/ContentHeader';

export const LearningModule = () => {
  const slug = useParams().slug ?? '';

  const moduleDetails = useGetModuleDetails(slug);
  const tableOfContents = useGetModuleTableOfContents(moduleDetails.data?.id ?? '');

  const isViewLoaded = !moduleDetails.isPending && moduleDetails.data && !tableOfContents.isPending && tableOfContents.data;

  return (
    <>
      {isViewLoaded ? (
        <LearningModuleContent moduleDetails={moduleDetails} slug={slug} tableOfContents={tableOfContents} />
      ) : (
        <LearningModuleLoadingSkeleton />
      )}
    </>
  );
};

const LearningModuleContent = ({ moduleDetails, slug, tableOfContents }) => {
  const service = useServices().learningHubService;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: isUserMember, isPending } = useIsUserMember();

  const markResourceAsDone = useMutation({
    mutationFn: (contentId: string) => service.markResourceAsDone(contentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['moduleContents', moduleDetails.data?.id] });
    },
  });

  const handleInitialSelectedEntry = () => {
    const initialSelectedEntry = getInitialEntry();
    if (
      initialSelectedEntry &&
      isLessonEntry(initialSelectedEntry) &&
      'isCompleted' in initialSelectedEntry &&
      !initialSelectedEntry?.isCompleted
    ) {
      markResourceAsDone.mutate(initialSelectedEntry.id);
    }
    return initialSelectedEntry;
  };

  const getInitialEntry = (): LearningModuleContentsEntry | undefined => {
    const entryIdFromUrl = searchParams.get('itemId');
    if (tableOfContents.data) {
      if (entryIdFromUrl) {
        return getSelectedEntry(tableOfContents.data.chapters, tableOfContents.data.certifications, entryIdFromUrl);
      } else {
        return getFirstEntryInModule(tableOfContents.data?.chapters ?? []);
      }
    }
  };

  const [selectedEntry, setSelectedEntry] = useState<LearningModuleContentsEntry | undefined>(() => handleInitialSelectedEntry());

  const selectEntry = (selectedEntry: LearningModuleContentsEntry) => {
    setSelectedEntry(selectedEntry);
    if (!!searchParams.get('itemId')) {
      searchParams.delete('itemId');
      setSearchParams(searchParams, { replace: true });
    }
    if (isLessonEntry(selectedEntry) && 'isCompleted' in selectedEntry && !selectedEntry?.isCompleted && !markResourceAsDone.isPending) {
      markResourceAsDone.mutate(selectedEntry.id);
    }
  };
  return (
    <>
      <LearningModuleHeader
        title={moduleDetails?.data?.title}
        shortDescription={moduleDetails?.data?.shortDescription}
        navigationPath={`/LearningHub/Modules/${slug}`}
        progress={tableOfContents?.data?.progress}
        isPending={moduleDetails.isPending && tableOfContents.isPending}
      ></LearningModuleHeader>

      <div className='learning-module-wrapper'>
        <div className='contents'>
          {selectedEntry && (
            <TableOfContents
              contents={tableOfContents?.data}
              customClasses='contents-in-module'
              selectedEntry={selectedEntry}
              handleEntrySelection={(newlySelectedEntry: LearningModuleContentsEntry) => selectEntry(newlySelectedEntry)}
            />
          )}
        </div>
        <div className='learning-module-content'>
          {selectedEntry && (
            <>
              <ContentHeader
                selectedEntry={selectedEntry}
                tableOfContents={tableOfContents.data}
                isUserMember={isUserMember}
                certificationTests={moduleDetails.data?.certificationTests}
                navigationDisabled={markResourceAsDone.isPending}
                handleSelectedEntry={(newlySelectedEntry: LearningModuleContentsEntry) => selectEntry(newlySelectedEntry)}
              />
              <div>
                {isChapterTestEntry(selectedEntry) && (
                  <ChapterTest
                    chapterTestId={selectedEntry?.id ?? ''}
                    moduleId={moduleDetails.data?.id ?? ''}
                    isCompleted={selectedEntry?.status === 'Completed'}
                  />
                )}
                {isCertificationEntry(selectedEntry) && (
                  <CertificationExam
                    isCompleted={selectedEntry?.status === 'Completed'}
                    title={selectedEntry?.title ?? ''}
                    certificationId={selectedEntry?.id ?? ''}
                    moduleId={moduleDetails.data?.id ?? ''}
                    progress={selectedEntry?.progress ?? 0}
                    badgeImageUrl={moduleDetails.data?.certificationTests.find((ct) => ct.id === selectedEntry?.id)?.badgeImageUrl ?? ''}
                  />
                )}
                {isLessonEntry(selectedEntry) && <LessonComponent content={selectedEntry as LearningModuleLesson | undefined} />}
              </div>
              {!isPending && !isUserMember && <GetPremiumCTA />}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const LearningModuleLoadingSkeleton = () => {
  return (
    <>
      <LearningModuleHeader title={''} shortDescription={''} navigationPath={''} progress={0} isPending={true}></LearningModuleHeader>

      <div className='learning-module-wrapper'>
        <div className='contents'>
          <Skeleton width='100%' height='100%' variant='rectangular' />
        </div>

        <div className='learning-module-content'>
          <Skeleton width={150} height={30} style={{ marginBottom: 20 }} variant='rounded' />
          <Skeleton width='100%' height='400px' variant='rounded' />
        </div>
      </div>
    </>
  );
};

type LessonProps = {
  content: LearningModuleLesson | undefined;
};

const LessonComponent = ({ content }: LessonProps) => {
  return (
    <>
      {content?.kind === 'Video' && <VideoLesson videoId={content.youTubeKey ?? ''} title={content.title} />}
      {content?.kind === 'Calculator' && (
        <CalculatorLesson
          calculatorKind={content.calculatorKind as CalculatorKind}
          calculatorOptions={JSON.parse(content.calculatorOptions ?? '')}
        />
      )}
      {content?.kind === 'Text' && <TextLesson text={content.text ?? ''} />}
      {content?.kind === 'Picture' && (
        <div className='picture-lesson'>
          <PictureLesson imageUrl={(content.medias && content.medias[0]?.fileUrl) ?? ''} imageTitle={content.title} />
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.text ?? '', { ADD_ATTR: ['target'] }) }}></div>
        </div>
      )}
    </>
  );
};
